import type { ContractSettingsRepository } from '../../application/ContractSettingsRepository'
import {
  activeContractSettingsVersion,
  emptyContractSettingsDocument,
  emptyContractSettingsGeneral,
  emptyContractSettingsOperations,
  emptyContractSettingsPricing,
  emptyContractSettingsService,
  type ContractClauseTemplate,
  type ContractSettingsClause,
  type ContractSettingsSeason,
  type ContractSettingsVersion,
} from '../../domain/contractSettings'

/**
 * Paramètres en mémoire, pour le mode fixtures et le repli de lecture.
 *
 * Rien n'est préchargé : une organisation qui n'a jamais configuré ses
 * paramètres n'en a pas, et en inventer ferait croire à une configuration.
 */
export function createFixtureContractSettingsRepository(
  source: 'fixtures' | 'fixture-fallback' = 'fixtures',
): ContractSettingsRepository {
  const seasons: ContractSettingsSeason[] = []
  const templates: ContractClauseTemplate[] = []

  function seasonById(seasonId: string): ContractSettingsSeason | undefined {
    return seasons.find((candidate) => candidate.id === seasonId)
  }

  /** Même composition qu'en base : les gabarits, dans leur rang, tous actifs. */
  function composeClauses(): ContractSettingsClause[] {
    return [...templates]
      .sort((left, right) => left.displayOrder - right.displayOrder)
      .map((template, index) => ({
        key: template.key,
        title: template.title,
        body: template.body,
        enabled: true,
        isLegal: template.isLegal,
        order: index + 1,
      }))
  }

  function seasonFromTemplates(label: string): ContractSettingsSeason {
    const season: ContractSettingsSeason = {
      id: crypto.randomUUID(),
      label,
      status: 'DRAFT',
      createdAt: new Date().toISOString(),
      versions: [],
    }
    const clauses = composeClauses()
    if (clauses.length > 0) {
      season.versions.push({
        id: crypto.randomUUID(),
        seasonId: season.id,
        version: 1,
        status: 'ACTIVE',
        general: emptyContractSettingsGeneral,
        service: emptyContractSettingsService,
        operations: emptyContractSettingsOperations,
        pricing: emptyContractSettingsPricing,
        document: emptyContractSettingsDocument,
        clauses,
        activatedAt: new Date().toISOString(),
      })
    }
    return season
  }

  return {
    listSeasons() {
      return Promise.resolve({
        seasons: seasons.map((season) => ({ ...season, versions: [...season.versions] })),
        meta: { source, retrievedAt: new Date().toISOString() },
      })
    },
    listClauseTemplates() {
      return Promise.resolve(
        [...templates]
          .sort((left, right) => left.displayOrder - right.displayOrder)
          .map((template) => ({ ...template })),
      )
    },
    upsertClauseTemplate(command) {
      const existing = templates.find((template) => template.key === command.key)
      if (existing) {
        existing.title = command.title
        existing.body = command.body
        existing.isLegal = command.isLegal
        return Promise.resolve()
      }
      templates.push({
        id: crypto.randomUUID(),
        key: command.key,
        title: command.title,
        body: command.body,
        isLegal: command.isLegal,
        displayOrder: templates.length + 1,
      })
      return Promise.resolve()
    },
    reorderClauseTemplates(keys) {
      if (keys.length !== templates.length)
        return Promise.reject(new Error('CONTRACT_CLAUSE_TEMPLATE_ORDER_INCOMPLETE'))
      for (const [index, key] of keys.entries()) {
        const template = templates.find((candidate) => candidate.key === key)
        if (!template) return Promise.reject(new Error('CONTRACT_CLAUSE_TEMPLATE_NOT_FOUND'))
        template.displayOrder = index + 1
      }
      return Promise.resolve()
    },
    createSeason(label) {
      if (seasons.some((season) => season.label === label))
        return Promise.reject(new Error('CONTRACT_SETTINGS_SEASON_LABEL_DUPLICATE'))
      const season = seasonFromTemplates(label)
      seasons.unshift(season)
      return Promise.resolve({ seasonId: season.id, label: season.label, status: season.status })
    },
    duplicateSeason(command) {
      if (command.sourceSeasonId === undefined) {
        if (seasons.some((season) => season.label === command.label))
          return Promise.reject(new Error('CONTRACT_SETTINGS_SEASON_LABEL_DUPLICATE'))
        const composed = seasonFromTemplates(command.label)
        seasons.unshift(composed)
        return Promise.resolve({
          seasonId: composed.id,
          label: composed.label,
          status: composed.status,
        })
      }
      const sourceSeason = seasonById(command.sourceSeasonId)
      if (!sourceSeason) return Promise.reject(new Error('CONTRACT_SETTINGS_SEASON_NOT_FOUND'))
      const active = activeContractSettingsVersion(sourceSeason)
      if (!active) return Promise.reject(new Error('CONTRACT_SETTINGS_SOURCE_VERSION_MISSING'))
      const season: ContractSettingsSeason = {
        id: crypto.randomUUID(),
        label: command.label,
        status: 'DRAFT',
        createdAt: new Date().toISOString(),
        versions: [],
      }
      season.versions.push({
        ...active,
        id: crypto.randomUUID(),
        seasonId: season.id,
        version: 1,
        status: 'ACTIVE',
        clauses: active.clauses.map((clause) => ({ ...clause })),
        activatedAt: new Date().toISOString(),
      })
      seasons.unshift(season)
      return Promise.resolve({ seasonId: season.id, label: season.label, status: season.status })
    },
    activateSeason(seasonId) {
      const season = seasonById(seasonId)
      if (!season) return Promise.reject(new Error('CONTRACT_SETTINGS_SEASON_NOT_FOUND'))
      for (const candidate of seasons) {
        if (candidate.status === 'ACTIVE') candidate.status = 'ARCHIVED'
      }
      season.status = 'ACTIVE'
      return Promise.resolve({ seasonId: season.id, label: season.label, status: season.status })
    },
    activateVersion(command) {
      const season = seasonById(command.seasonId)
      if (!season) return Promise.reject(new Error('CONTRACT_SETTINGS_SEASON_NOT_FOUND'))
      const replayed = season.versions.find((version) => version.id === command.idempotencyKey)
      if (replayed) {
        return Promise.resolve({
          versionId: replayed.id,
          seasonId: season.id,
          version: replayed.version,
          status: replayed.status,
          activatedAt: replayed.activatedAt,
        })
      }
      for (const version of season.versions) version.status = 'RETIRED'
      const version: ContractSettingsVersion = {
        id: command.idempotencyKey,
        seasonId: season.id,
        version: season.versions.length + 1,
        status: 'ACTIVE',
        general: command.general,
        service: command.service,
        operations: command.operations,
        pricing: command.pricing,
        document: command.document,
        clauses: command.clauses.map((clause) => ({ ...clause })),
        activatedAt: new Date().toISOString(),
      }
      season.versions.unshift(version)
      return Promise.resolve({
        versionId: version.id,
        seasonId: season.id,
        version: version.version,
        status: version.status,
        activatedAt: version.activatedAt,
      })
    },
  }
}
